import apiClient from '../axiosConfig';
import { components } from '../types/openapi';

// Tipos del OpenAPI
type CreateUser = components['schemas']['CreateUser'];
type UpdateUser = components['schemas']['UpdateUser'];
type UpdateUserApp = components['schemas']['UpdateUserApp'];
type UserResponse = components['schemas']['UserResponse'];
type UserSimpleResponse = components['schemas']['UserSimpleResponse'];
type PaginationUserSimpleResponse = components['schemas']['Pagination_UserSimpleResponse_'];
type ChangePassword = components['schemas']['ChangePassword'];

/**
 * Servicio de gestión de usuarios
 * Maneja todas las operaciones CRUD de usuarios
 * Basado en los endpoints del OpenAPI
 */
class UserService {
  /**
   * Obtener lista de usuarios
   * GET /api/users/
   * Solo para administradores
   */
  async getUsers(params?: {
    limit?: number;
    name?: string;
    username?: string;
    email?: string;
    phone?: string;
  }): Promise<UserSimpleResponse[]> {
    const response = await apiClient.get<UserSimpleResponse[]>('/users/', { params });
    return response.data;
  }

  /**
   * Obtener usuarios con paginación
   * GET /api/users/pagination
   * Solo para administradores
   */
  async getUsersPaginated(params?: {
    page?: number;
    limit?: number;
    name?: string;
    username?: string;
    email?: string;
    phone?: string;
  }): Promise<PaginationUserSimpleResponse> {
    const response = await apiClient.get<PaginationUserSimpleResponse>('/users/pagination', { params });
    return response.data;
  }

  /**
   * Obtener un usuario por ID
   * GET /api/users/{user_id}
   * Solo para administradores
   */
  async getUserById(userId: number): Promise<UserResponse> {
    const response = await apiClient.get<UserResponse>(`/users/${userId}`);
    return response.data;
  }

  /**
   * Crear un nuevo usuario
   * POST /api/users/
   * Solo para administradores
   */
  async createUser(userData: CreateUser): Promise<UserResponse> {
    const response = await apiClient.post<UserResponse>('/users/', userData);
    return response.data;
  }

  /**
   * Actualizar un usuario
   * PUT /api/users/
   * Solo para administradores
   */
  async updateUser(userData: UpdateUser): Promise<UserResponse> {
    const response = await apiClient.put<UserResponse>('/users/', userData);
    return response.data;
  }

  /**
   * Eliminar un usuario
   * DELETE /api/users/{user_id}
   * Solo para administradores
   */
  async deleteUser(userId: number): Promise<void> {
    await apiClient.delete(`/users/${userId}`);
  }

  /**
   * Cambiar estado de un usuario (activar/desactivar)
   * POST /api/users/change-state/{user_id}
   * Solo para administradores
   */
  async changeUserState(userId: number): Promise<UserResponse> {
    const response = await apiClient.post<UserResponse>(`/users/change-state/${userId}`);
    return response.data;
  }

  /**
   * Cambiar contraseña de un usuario (admin)
   * PUT /api/users/change-password
   * Solo para administradores
   */
  async changeUserPassword(userId: number, password: string): Promise<void> {
    const body: ChangePassword = { id: userId, password };
    await apiClient.put('/users/change-password', body);
  }

  /**
   * Obtener colectores activos
   * GET /api/users/collectors/
   * ⚠️ ADVERTENCIA: Este endpoint no aparece en el OpenAPI regenerado.
   * Si el endpoint fue removido, este método fallará.
   * Considerar usar getUsers() con filtros de rol en su lugar.
   */
  async getActiveCollectors(): Promise<UserSimpleResponse[]> {
    const response = await apiClient.get<UserSimpleResponse[]>('/users/collectors/');
    return response.data;
  }

  /**
   * Obtener abogados activos
   * GET /api/users/lawyers/
   */
  /**
   * Obtener abogados activos
   * GET /api/users/lawyers/
   * Según OpenAPI: read_lawyers_active_api_users_lawyers__get
   * Devuelve: UserSimpleResponse[]
   */
  async getActiveLawyers(): Promise<UserSimpleResponse[]> {
    if (process.env.NODE_ENV === 'development') {
      console.log('📤 UserService.getActiveLawyers - Llamando endpoint según OpenAPI:');
      console.log('   Path: /api/users/lawyers/');
      console.log('   Operation: read_lawyers_active_api_users_lawyers__get');
      console.log('   Expected: UserSimpleResponse[]');
    }
    
    try {
      // El endpoint según OpenAPI es /api/users/lawyers/
      // Con baseURL = /api/, la ruta completa es /users/lawyers/
      const response = await apiClient.get<UserSimpleResponse[]>('/users/lawyers/');
      
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ UserService.getActiveLawyers - Respuesta recibida:', {
          status: response.status,
          statusText: response.statusText,
          headers: response.headers,
          dataType: Array.isArray(response.data) ? 'array' : typeof response.data,
          dataLength: Array.isArray(response.data) ? response.data.length : 'N/A',
          data: response.data,
          fullUrl: response.config?.url || 'N/A',
        });
      }
      
      // Asegurarse de que siempre devolvemos un array
      if (!response.data) {
        console.warn('⚠️ UserService.getActiveLawyers - Respuesta vacía o null');
        return [];
      }
      
      if (!Array.isArray(response.data)) {
        console.error('❌ UserService.getActiveLawyers - La respuesta no es un array:', {
          receivedType: typeof response.data,
          receivedData: response.data,
        });
        return [];
      }
      
      return response.data;
    } catch (error: any) {
      if (process.env.NODE_ENV === 'development') {
        console.error('❌ UserService.getActiveLawyers - Error completo:', {
          message: error.message,
          code: error.code,
          response: {
            data: error.response?.data,
            status: error.response?.status,
            statusText: error.response?.statusText,
            headers: error.response?.headers,
          },
          request: {
            url: error.config?.url,
            method: error.config?.method,
            baseURL: error.config?.baseURL,
            headers: error.config?.headers,
          },
        });
      }
      throw error;
    }
  }

  /**
   * Actualizar perfil del usuario actual
   * PUT /api/users/
   * Ahora usa el esquema UpdateUser que requiere id, role, username
   */
  async updateProfile(userData: UpdateUser): Promise<UserResponse> {
    console.log('UserService.updateProfile - Datos recibidos:', JSON.stringify(userData, null, 2));
    console.log('UserService.updateProfile - URL completa:', `${apiClient.defaults.baseURL}/users/`);
    
    try {
      const response = await apiClient.put<UserResponse>('/users/', userData);
      console.log('UserService.updateProfile - Respuesta exitosa:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('UserService.updateProfile - Error completo:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          data: error.config?.data,
          headers: error.config?.headers,
        },
      });
      throw error;
    }
  }

  /**
   * Cambiar contraseña del perfil del usuario actual
   * PUT /api/users/me/change-password
   */
  async changeProfilePassword(newPassword: string): Promise<void> {
    const body = { password: newPassword };
    await apiClient.put('/users/me/change-password', body);
  }

  /**
   * Actualizar imagen de perfil del usuario actual
   * POST /api/users/me/image-profile
   */
  async updateProfileImage(file: File): Promise<UserResponse> {
    const formData = new FormData();
    formData.append('file', file);
    // IMPORTANTE: NO establecer Content-Type manualmente para FormData
    // El navegador lo establecerá automáticamente con el boundary correcto
    const response = await apiClient.post<UserResponse>('/users/me/image-profile', formData);
    return response.data;
  }
}

export default new UserService();

